/**
 * Kanban Board Component
 * Displays jobs as cards in phase-based columns with drag-and-drop
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { api } from '../services/api';
import { JobCard } from './JobCard';
import type { JobEditorTabId } from './JobDescriptionEditor';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useApplyGate } from '../hooks/useApplyGate';
import type { UserJob, JobPhase, SubcomponentType, UserPreferences, EditorFocusTarget } from '../types';
import { ACTIVE_PHASES } from '../types';
import { PHASE_COLORS } from '../constants';
import {
  MapPin,
  Calendar,
  ExternalLink,
  GripVertical,
  Zap,
  FileCheck,
  ChevronDown,
  ChevronRight,
  Send,
  PhoneForwarded,
  MessageSquare,
  Loader2,
  PartyPopper,
  Trash2,
} from 'lucide-react';

// Phases to show as kanban columns (the main workflow pipeline)
const KANBAN_PHASES: JobPhase[] = [...ACTIVE_PHASES, 'Declined', 'Accepted'];

// Terminal phases collapsed into a "done" bucket
const TERMINAL_PHASES: JobPhase[] = ['Skipped', 'Expired', 'Errored', 'Trash'];

// Phase column accent colors (border-top and drop highlight)
const PHASE_ACCENTS: Record<string, string> = {
  'Queued': 'border-t-yellow-400',
  'Generating': 'border-t-purple-400',
  'Ready': 'border-t-green-400',
  'Applied': 'border-t-indigo-400',
  'Follow-Up': 'border-t-orange-400',
  'Interviewing': 'border-t-pink-400',
  'Declined': 'border-t-rose-400',
  'Accepted': 'border-t-emerald-400',
};

const DROP_HIGHLIGHTS: Record<string, string> = {
  'Queued': 'bg-yellow-50',
  'Generating': 'bg-purple-50',
  'Ready': 'bg-green-50',
  'Applied': 'bg-indigo-50',
  'Follow-Up': 'bg-orange-50',
  'Interviewing': 'bg-pink-50',
  'Declined': 'bg-rose-50',
  'Accepted': 'bg-emerald-50',
};

interface KanbanBoardProps {
  jobs: UserJob[];
  phaseCounts: Record<JobPhase, number>;
  onRefresh: () => void;
  onEditSubcomponent: (jobid: string, component: SubcomponentType) => void;
  onEditJob?: (jobid: string, initialTab?: JobEditorTabId) => void;
  onGenerateResume?: (jobid: string) => void;
  onOptimisticPhaseChange?: (jobid: string, newPhase: JobPhase) => void;
  onJobUpdate?: (updatedJob: UserJob) => void;
  selectedResume?: string | null;
  autoDownloadFiles?: boolean;
  userPreferences?: UserPreferences;
  onUpgradeRequest?: (reason: 'exhausted' | 'enable_job' | 'regen_section') => void;
  onFreeGenConsumed?: () => void;
  /** Open the selected master resume in Edit Resume (Recruiter Check deep-link). */
  onEditResume?: (target?: Omit<EditorFocusTarget, 'nonce'>) => void;
}

export function KanbanBoard({
  jobs,
  phaseCounts,
  onRefresh,
  onEditSubcomponent,
  onEditJob,
  onGenerateResume,
  onOptimisticPhaseChange,
  onJobUpdate,
  selectedResume,
  autoDownloadFiles,
  userPreferences,
  onUpgradeRequest,
  onFreeGenConsumed,
  onEditResume,
}: KanbanBoardProps) {
  // Recruiter Check apply gate (BR-7). ONE instance for the whole board: the
  // drop handler and every card's quick-Apply go through the same confirm.
  const { guardPhaseMove, applyGateDialog } = useApplyGate();
  const [draggedJob, setDraggedJob] = useState<UserJob | null>(null);
  const [dropTarget, setDropTarget] = useState<JobPhase | null>(null);
  const [movingJobId, setMovingJobId] = useState<string | null>(null);
  const [showTerminal, setShowTerminal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const dragCounter = useRef<Map<JobPhase, number>>(new Map());
  const jobModalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(jobModalRef, !!selectedJobId);

  // Close modal on ESC
  useEffect(() => {
    if (!selectedJobId) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedJobId(null);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [selectedJobId]);

  const selectedJob = selectedJobId ? jobs.find(j => j.jobid === selectedJobId) : null;

  // Group jobs by phase
  const jobsByPhase = KANBAN_PHASES.reduce((acc, phase) => {
    acc[phase] = jobs.filter(j => j.jobphase === phase);
    return acc;
  }, {} as Record<JobPhase, UserJob[]>);

  const terminalJobs = jobs.filter(j => TERMINAL_PHASES.includes(j.jobphase));
  const terminalCount = terminalJobs.length;

  // Drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, job: UserJob) => {
    setDraggedJob(job);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', job.jobid);
    // Make the drag image slightly transparent
    const el = e.currentTarget as HTMLElement;
    setTimeout(() => el.classList.add('opacity-40'), 0);
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    const el = e.currentTarget as HTMLElement;
    el.classList.remove('opacity-40');
    setDraggedJob(null);
    setDropTarget(null);
    dragCounter.current.clear();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent, phase: JobPhase) => {
    e.preventDefault();
    const count = (dragCounter.current.get(phase) || 0) + 1;
    dragCounter.current.set(phase, count);
    setDropTarget(phase);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent, phase: JobPhase) => {
    e.preventDefault();
    const count = (dragCounter.current.get(phase) || 0) - 1;
    dragCounter.current.set(phase, count);
    if (count <= 0) {
      dragCounter.current.set(phase, 0);
      setDropTarget(prev => prev === phase ? null : prev);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, targetPhase: JobPhase) => {
    e.preventDefault();
    setDropTarget(null);
    dragCounter.current.clear();

    if (!draggedJob || draggedJob.jobphase === targetPhase) {
      setDraggedJob(null);
      return;
    }

    const movedJobId = draggedJob.jobid;
    const previousPhase = draggedJob.jobphase;
    const movedJob = draggedJob;
    setDraggedJob(null);

    // Recruiter Check (BR-7): dropping a C/D-graded resume into Applied asks
    // first. The OPTIMISTIC MOVE IS DEFERRED until the confirm resolves —
    // moving the card and then asking would show it in Applied while the user
    // is still deciding, and a cancel would have to yank it back. Every other
    // drop resolves immediately, so ordinary drag-drop is unchanged.
    const decision = await guardPhaseMove(movedJob, targetPhase);
    if (!decision.proceed) return;

    // Optimistic update — move the card instantly in the UI
    if (onOptimisticPhaseChange) {
      onOptimisticPhaseChange(movedJobId, targetPhase);
    }

    setMovingJobId(movedJobId);

    try {
      const updatedUserJob = await api.updateJobPhase(movedJobId, targetPhase, decision.recruiterAck);
      // "Apply anyway" is remembered on the card so it never re-asks.
      if (decision.recruiterAck && onJobUpdate) {
        onJobUpdate({ ...movedJob, jobphase: targetPhase, recruiter_ack: '1' });
      }
      // Dropping into Interviewing computes the interview-question matches; clear
      // the transient "matching…" marker the optimistic move stamped on.
      if (targetPhase === 'Interviewing' && onJobUpdate) {
        onJobUpdate({
          ...movedJob,
          jobphase: 'Interviewing',
          interview_matches: updatedUserJob?.interview_matches ?? [],
          interview_matched_at: updatedUserJob?.interview_matched_at,
          interview_match_state: 'done',
        });
      }
    } catch (error) {
      console.error('Failed to move job:', error);
      // Revert on failure
      if (onOptimisticPhaseChange) {
        onOptimisticPhaseChange(movedJobId, previousPhase);
      }
    } finally {
      setTimeout(() => setMovingJobId(null), 300);
    }
  }, [draggedJob, onOptimisticPhaseChange, onJobUpdate, guardPhaseMove]);

  return (
    <div className="h-full flex flex-col">
      {/* Kanban columns */}
      <div className="flex-1 flex gap-3 overflow-x-auto pb-4 px-2 min-h-0">
        {KANBAN_PHASES.map(phase => {
          const phaseJobs = jobsByPhase[phase] || [];
          const isDropping = dropTarget === phase && draggedJob?.jobphase !== phase;

          return (
            <div
              key={phase}
              className={`flex-shrink-0 w-64 flex flex-col rounded-lg border-t-4 bg-white border border-gray-200 shadow-sm transition-all duration-200 ${
                PHASE_ACCENTS[phase] || 'border-t-gray-400'
              } ${isDropping ? `${DROP_HIGHLIGHTS[phase] || 'bg-gray-50'} ring-2 ring-primary-300 scale-[1.01]` : ''}`}
              onDragEnter={(e) => handleDragEnter(e, phase)}
              onDragLeave={(e) => handleDragLeave(e, phase)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, phase)}
            >
              {/* Column header */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-700">{phase}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PHASE_COLORS[phase]}`}>
                  {phaseCounts[phase] || 0}
                </span>
              </div>

              {/* Cards */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[100px]">
                {phaseJobs.length === 0 && !isDropping && (
                  <div className="flex items-center justify-center h-20 text-gray-300 text-xs">
                    No jobs
                  </div>
                )}
                {isDropping && phaseJobs.length === 0 && (
                  <div className="flex items-center justify-center h-20 border-2 border-dashed border-primary-300 rounded-lg text-primary-400 text-xs">
                    Drop here
                  </div>
                )}
                {phaseJobs.map(job => (
                  <KanbanCard
                    key={job.jobid}
                    job={job}
                    isMoving={movingJobId === job.jobid}
                    isDragging={draggedJob?.jobid === job.jobid}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onGenerateResume={onGenerateResume}
                    onRefresh={onRefresh}
                    onCardClick={() => setSelectedJobId(job.jobid)}
                    onOptimisticPhaseChange={onOptimisticPhaseChange}
                    onJobUpdate={onJobUpdate}
                    selectedResume={selectedResume}
                    autoDownloadFiles={autoDownloadFiles}
                    guardPhaseMove={guardPhaseMove}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Terminal phases collapse */}
      {terminalCount > 0 && (
        <div className="border-t border-gray-200 bg-white px-4 py-2">
          <button
            onClick={() => setShowTerminal(!showTerminal)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
          >
            {showTerminal ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <span>Archived ({terminalCount})</span>
            <span className="text-xs text-gray-500">Skipped, Expired, Errored, Trash</span>
          </button>
          {showTerminal && (
            <div className="mt-2 flex flex-wrap gap-2 max-h-48 overflow-y-auto">
              {terminalJobs.map(job => (
                <div
                  key={job.jobid}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded border border-gray-200 text-xs"
                >
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${PHASE_COLORS[job.jobphase]}`}>
                    {job.jobphase}
                  </span>
                  <span className="font-medium text-gray-700 truncate max-w-[150px]">{job.jobcompany}</span>
                  <span className="text-gray-500 truncate max-w-[120px]">{job.jobtitle}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Full JobCard modal */}
      {selectedJob && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setSelectedJobId(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`${selectedJob.jobcompany} — ${selectedJob.jobtitle}`}
        >
          <div
            ref={jobModalRef}
            className="w-full max-w-md animate-in"
            onClick={(e) => e.stopPropagation()}
          >
            <JobCard
              job={selectedJob}
              onRefresh={() => { onRefresh(); }}
              onEditSubcomponent={onEditSubcomponent}
              onEditJob={onEditJob}
              onGenerateResume={onGenerateResume}
              onJobUpdate={onJobUpdate}
              onOptimisticPhaseChange={onOptimisticPhaseChange}
              selectedResume={selectedResume}
              autoDownloadFiles={autoDownloadFiles}
              userPreferences={userPreferences}
              onUpgradeRequest={onUpgradeRequest}
              onFreeGenConsumed={onFreeGenConsumed}
              onEditResume={onEditResume}
            />
          </div>
        </div>
      )}

      {/* Apply-time nudge (BR-7) — one dialog for the whole board. */}
      {applyGateDialog}
    </div>
  );
}

// Social links for the "Congrats" action (Accepted phase)
const SOCIAL_LINKS = [
  {
    label: 'LinkedIn',
    url: 'https://www.linkedin.com/company/skillsnap-me',
    shareUrl: 'https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fskillsnap.me',
  },
];

// Compact card for kanban columns
interface KanbanCardProps {
  job: UserJob;
  isMoving: boolean;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, job: UserJob) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onGenerateResume?: (jobid: string) => void;
  onRefresh: () => void;
  onCardClick: () => void;
  onOptimisticPhaseChange?: (jobid: string, newPhase: JobPhase) => void;
  onJobUpdate?: (updatedJob: UserJob) => void;
  selectedResume?: string | null;
  autoDownloadFiles?: boolean;
  /** Recruiter Check apply gate (BR-7), owned by the board so every card and
   *  the drop handler share ONE confirm. Resolves instantly for any move that
   *  doesn't warrant asking. */
  guardPhaseMove: (job: UserJob, phase: JobPhase) => Promise<{ proceed: boolean; recruiterAck: boolean }>;
}

function KanbanCard({
  job,
  isMoving,
  isDragging,
  onDragStart,
  onDragEnd,
  onGenerateResume,
  onRefresh,
  onCardClick,
  onOptimisticPhaseChange,
  onJobUpdate,
  selectedResume,
  autoDownloadFiles,
  guardPhaseMove,
}: KanbanCardProps) {
  const [generating, setGenerating] = useState(false);

  const hasAnyFile = !!(job.s3locresumehtml || job.s3locresumepdf);
  const hasCompleteSections = (['contact', 'summary', 'skills', 'experience'] as const).some(
    comp => {
      const data = job[`data${comp}` as keyof UserJob] as string | undefined;
      return !!data && data.trim().length > 0;
    }
  );

  async function handleQuickGenerate(e: React.MouseEvent) {
    e.stopPropagation();
    const resumeid = selectedResume || job.resumeid;
    if (!resumeid) {
      alert('Please select a resume before generating. Use the resume dropdown in the header.');
      return;
    }
    setGenerating(true);
    try {
      const components = (['contact', 'summary', 'skills', 'highlights', 'experience', 'education', 'awards', 'presentations', 'publications', 'portfolios', 'coverletter'] as const).filter(comp => {
        const type = job[`type${comp}` as keyof UserJob] as string;
        return type !== 'omit';
      });
      await api.generateAll(job.jobid, [...components], resumeid);
      onGenerateResume?.(job.jobid);
      setTimeout(() => {
        onRefresh();
        setGenerating(false);
      }, 500);
    } catch {
      setGenerating(false);
    }
  }

  // Track mouse movement to distinguish click from drag
  const mouseStart = useRef<{ x: number; y: number } | null>(null);
  const wasDragged = useRef(false);

  function handleMouseDown(e: React.MouseEvent) {
    mouseStart.current = { x: e.clientX, y: e.clientY };
    wasDragged.current = false;
  }

  function handleMouseUp(e: React.MouseEvent) {
    if (!mouseStart.current) return;
    const dx = Math.abs(e.clientX - mouseStart.current.x);
    const dy = Math.abs(e.clientY - mouseStart.current.y);
    // Only count as click if mouse barely moved (< 5px)
    if (dx < 5 && dy < 5 && !wasDragged.current) {
      onCardClick();
    }
    mouseStart.current = null;
  }

  function handleDragStartWrapper(e: React.DragEvent) {
    wasDragged.current = true;
    onDragStart(e, job);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onCardClick();
    }
  }

  return (
    <div
      draggable
      tabIndex={0}
      role="button"
      aria-label={`${job.jobcompany} — ${job.jobtitle}. Press Enter to view details.`}
      onDragStart={handleDragStartWrapper}
      onDragEnd={onDragEnd}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onKeyDown={handleKeyDown}
      className={`group relative bg-white border rounded-lg p-2.5 cursor-grab active:cursor-grabbing transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
        isDragging ? 'opacity-40 scale-95' : ''
      } ${isMoving ? 'animate-pulse border-primary-300' : 'border-gray-200'} hover:shadow-md hover:border-gray-300`}
    >
      {/* Drag handle */}
      <div className="absolute right-1 top-1 opacity-0 group-hover:opacity-40 transition-opacity">
        <GripVertical className="w-3.5 h-3.5 text-gray-500" />
      </div>

      {/* Company */}
      <p className="text-sm font-bold text-gray-800 truncate pr-4">{job.jobcompany}</p>

      {/* Title */}
      <p className="text-xs text-gray-600 truncate mt-0.5">
        {job.joburl ? (
          <a
            href={job.joburl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary-600 inline-flex items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="truncate">{job.jobtitle}</span>
            <ExternalLink className="w-2.5 h-2.5 ml-0.5 flex-shrink-0" />
          </a>
        ) : (
          job.jobtitle
        )}
      </p>

      {/* Meta row */}
      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-gray-500">
        {job.joblocation && (
          <span className="flex items-center truncate">
            <MapPin className="w-2.5 h-2.5 mr-0.5 flex-shrink-0" />
            {job.joblocation}
          </span>
        )}
        <span className="flex items-center">
          <Calendar className="w-2.5 h-2.5 mr-0.5 flex-shrink-0" />
          {job.postingAge !== undefined ? `${job.postingAge}d` : job.jobposteddate}
        </span>
      </div>

      {/* Tags */}
      {job.jobtags && job.jobtags.length > 0 && (
        <div className="flex flex-wrap gap-0.5 mt-1.5">
          {job.jobtags.slice(0, 2).map(tag => (
            <span key={tag} className="px-1 py-0.5 bg-gray-100 text-gray-500 text-[9px] rounded">
              {tag}
            </span>
          ))}
          {job.jobtags.length > 2 && (
            <span className="text-[9px] text-gray-500">+{job.jobtags.length - 2}</span>
          )}
        </div>
      )}

      {/* Status indicators & quick actions */}
      <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-gray-100">
        <div className="flex items-center gap-1" role="status" aria-live="polite">
          {hasCompleteSections && (
            <span className="flex items-center text-[10px] text-green-600">
              <FileCheck className="w-3 h-3 mr-0.5" />
              Sections
            </span>
          )}
          {hasAnyFile && (
            <span className="flex items-center text-[10px] text-green-600">
              <FileCheck className="w-3 h-3 mr-0.5" />
              Files
            </span>
          )}
          {!hasCompleteSections && !hasAnyFile && (
            <span className="text-[10px] text-gray-300">Not generated</span>
          )}
        </div>

        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          {(() => {
            const phase = job.jobphase;

            // Helper: get PDF URL from s3loc field
            function getPdfUrl(s3loc?: string): string | null {
              if (!s3loc) return null;
              const path = s3loc.replace(/^s3:\/\/skillsnap-public-resumes\//, '');
              const parts = path.split('/');
              if (parts.length >= 4) {
                const [username, company, jobtitlesafe, ...rest] = parts;
                return `https://${username}.skillsnap.me/${company}/${jobtitlesafe}/${rest.join('/')}`;
              }
              return `https://d2qz6zc5zvuvjc.cloudfront.net/${path}`;
            }

            // Helper: move job to a phase. Routed through the shared apply gate
            // (BR-7) so the quick-Apply action can't slip past the confirm the
            // drag-drop path and the full card both honour; the optimistic move
            // waits for the decision.
            async function moveToPhase(e: React.MouseEvent, targetPhase: JobPhase) {
              e.stopPropagation();
              const decision = await guardPhaseMove(job, targetPhase);
              if (!decision.proceed) return;
              onOptimisticPhaseChange?.(job.jobid, targetPhase);
              try {
                const updatedUserJob = await api.updateJobPhase(job.jobid, targetPhase, decision.recruiterAck);
                if (decision.recruiterAck && onJobUpdate) {
                  onJobUpdate({ ...job, jobphase: targetPhase, recruiter_ack: '1' });
                }
                // Moving into Interviewing computes interview-question matches;
                // clear the transient "matching…" marker on success.
                if (targetPhase === 'Interviewing' && onJobUpdate) {
                  onJobUpdate({
                    ...job,
                    jobphase: 'Interviewing',
                    interview_matches: updatedUserJob?.interview_matches ?? [],
                    interview_matched_at: updatedUserJob?.interview_matched_at,
                    interview_match_state: 'done',
                  });
                }
              } catch {
                onRefresh();
              }
            }

            // Helper: open PDF (view + download like JobCard)
            function openPdf(e: React.MouseEvent, url: string, suffix: string) {
              e.stopPropagation();
              if (!autoDownloadFiles) {
                window.open(url, '_blank');
                return;
              }
              // Auto-download on: just download the PDF, do not also open it
              // in an in-browser viewer tab.
              const company = (job.jobcompany || 'company').replace(/\s+/g, '').replace(/[,\.]/g, '');
              const candidateName = (job.dataname || 'user').replace(/\s+/g, '');
              const downloadName = `${company}.${candidateName}.${suffix}.pdf`;
              fetch(url)
                .then(res => res.blob())
                .then(blob => {
                  const blobUrl = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = blobUrl;
                  a.download = downloadName;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                })
                .catch(() => {});
            }

            const btnBase = 'flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors';

            if (phase === 'Queued' || phase === 'Generating') {
              return (
                <button
                  onClick={handleQuickGenerate}
                  disabled={generating}
                  className={`${btnBase} hover:bg-primary-50 text-primary-600 disabled:opacity-50`}
                  title="Generate Resume"
                >
                  <Zap className={`w-3 h-3 ${generating ? 'animate-pulse' : ''}`} />
                  <span>Generate</span>
                </button>
              );
            }

            if (phase === 'Ready') {
              return (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Open job URL
                    if (job.joburl) window.open(job.joburl, '_blank');
                    // Open + download Resume PDF
                    const resumeUrl = getPdfUrl(job.s3locresumepdf);
                    if (resumeUrl) openPdf(e, resumeUrl, 'resume');
                    // Open + download Cover Letter PDF
                    const coverUrl = getPdfUrl(job.s3loccoverletterpdf);
                    if (coverUrl) openPdf(e, coverUrl, 'coverletter');
                    // Move to Applied
                    moveToPhase(e, 'Applied');
                  }}
                  className={`${btnBase} hover:bg-indigo-50 text-indigo-600`}
                  title="Open job posting, resume, and cover letter"
                >
                  <Send className="w-3 h-3" />
                  <span>Apply</span>
                </button>
              );
            }

            if (phase === 'Applied') {
              return (
                <button
                  onClick={(e) => moveToPhase(e, 'Follow-Up')}
                  className={`${btnBase} hover:bg-orange-50 text-orange-600`}
                  title="Mark for Follow-Up"
                >
                  <PhoneForwarded className="w-3 h-3" />
                  <span>Follow-Up</span>
                </button>
              );
            }

            if (phase === 'Follow-Up') {
              return (
                <button
                  onClick={(e) => moveToPhase(e, 'Interviewing')}
                  className={`${btnBase} hover:bg-pink-50 text-pink-600`}
                  title="Mark for Interview"
                >
                  <PhoneForwarded className="w-3 h-3" />
                  <span>Interview</span>
                </button>
              );
            }

            if (phase === 'Interviewing') {
              const matching = job.interview_match_state === 'matching';
              return (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (matching) return;
                    window.open(`${window.location.origin}/interview-qa?job=${job.jobid}`, '_blank');
                  }}
                  disabled={matching}
                  className={`${btnBase} hover:bg-pink-50 text-pink-600`}
                  title={matching ? 'Matching interview questions…' : 'Practice interview questions'}
                >
                  {matching ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
                  <span>{matching ? 'Matching…' : 'Practice'}</span>
                </button>
              );
            }

            if (phase === 'Accepted') {
              return (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    for (const social of SOCIAL_LINKS) {
                      window.open(social.shareUrl || social.url, '_blank');
                    }
                  }}
                  className={`${btnBase} hover:bg-emerald-50 text-emerald-600`}
                  title="Congrats! Give Snappy some props!"
                >
                  <PartyPopper className="w-3 h-3" />
                  <span>Congrats!</span>
                </button>
              );
            }

            // All other phases: Skipped, Expired, Errored, Declined, Trash
            return (
              <button
                onClick={(e) => moveToPhase(e, 'Trash')}
                className={`${btnBase} hover:bg-red-50 text-red-400`}
                title="Mark for Deletion"
              >
                <Trash2 className="w-3 h-3" />
                <span>Delete</span>
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
